import { z } from 'zod';
import { INVESTMENT_COUNTRY_CODES, InvestmentStatus, InvestmentType } from '@inversor-hub/types';

export const investmentStatusOptions = [
  'PURCHASING',
  'RENOVATING',
  'LISTED_FOR_RENT_OR_SALE',
  'RENTED',
  'SOLD',
] as const satisfies readonly InvestmentStatus[];

export const handledInvestmentTypeOptions = [
  'TRADITIONAL_RENT',
  'ROOM_RENT',
  'ROOM_SALE',
] as const satisfies readonly InvestmentType[];

export const financingDecisionOptions = ['PENDING', 'YES', 'NO'] as const;
export const financingInputModeOptions = ['MANUAL_PAYMENT', 'ESTIMATE_PAYMENT'] as const;
export type FinancingDecision = (typeof financingDecisionOptions)[number];
export type FinancingInputMode = (typeof financingInputModeOptions)[number];

const requiredString = (message: string) => z.string().trim().min(1, message);

const nullableMoney = z.number().int().nonnegative().nullable();
const nullableRate = z.number().min(0).max(1).nullable();
const nullablePositiveInt = z.number().int().positive().nullable();

export const investmentFormSchema = z
  .object({
    alias: requiredString('El alias es obligatorio'),
    identificationCode: z.string().trim().max(40, 'Máximo 40 caracteres'),
    country: z.enum(INVESTMENT_COUNTRY_CODES, {
      errorMap: () => ({ message: 'Selecciona un país de la zona euro' }),
    }),
    address: requiredString('La dirección es obligatoria'),
    city: requiredString('La población es obligatoria'),
    postalCode: z.string(),
    province: requiredString('La provincia es obligatoria'),
    type: z.enum(handledInvestmentTypeOptions, {
      errorMap: () => ({ message: 'La estrategia es obligatoria' }),
    }),
    status: z.enum(investmentStatusOptions, {
      errorMap: () => ({ message: 'El estado de la inversión es obligatorio' }),
    }),
    purchaseDate: requiredString('La fecha de compra o escritura es obligatoria'),
    ownershipPercentage: nullableRate,
    acquisitionPrice: nullablePositiveInt,
    renovationCosts: nullableMoney,
    furnitureEquipmentCosts: nullableMoney,
    initialCostComplete: z.boolean(),
    purchaseCostsTotal: nullableMoney,
    itpTax: nullableMoney,
    notaryFees: nullableMoney,
    registryFees: nullableMoney,
    agencyFees: nullableMoney,
    legalFees: nullableMoney,
    otherTransactionCosts: nullableMoney,
    rentedSince: z.string(),
    monthlyRent: nullableMoney,
    monthlyOpexTotal: nullableMoney,
    ibiTax: nullableMoney,
    communityFee: nullableMoney,
    supplyCosts: nullableMoney,
    maintenanceCosts: nullableMoney,
    wasteTax: nullableMoney,
    homeInsurance: nullableMoney,
    rentInsurance: nullableMoney,
    otherOpex: nullableMoney,
    financingDecision: z.enum(financingDecisionOptions),
    financingInputMode: z.union([z.literal(''), z.enum(financingInputModeOptions)]),
    monthlyDebtService: nullableMoney,
    loanAmount: nullableMoney,
    termYears: z.number().int().positive().nullable(),
    rateType: z.union([z.literal(''), z.enum(['FIXED', 'VARIABLE'])]),
    fixedRate: nullableRate,
    variableSpread: nullableRate,
    variableEuribor: nullableRate,
    mainImageFile: z.custom<File | null | undefined>(() => true).optional(),
    removeMainImage: z.boolean().optional(),
    mainImageUrl: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.ownershipPercentage === null || data.ownershipPercentage <= 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['ownershipPercentage'],
        message: 'La titularidad debe ser mayor que 0%',
      });
    }

    if (data.acquisitionPrice === null || data.acquisitionPrice <= 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['acquisitionPrice'],
        message: 'El precio de compra es obligatorio',
      });
    }

    if (data.financingDecision !== 'YES') {
      return;
    }

    if (!data.financingInputMode) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['financingInputMode'],
        message: 'Selecciona cómo quieres indicar la financiación',
      });
      return;
    }

    if (data.financingInputMode === 'MANUAL_PAYMENT') {
      if (data.monthlyDebtService === null || data.monthlyDebtService <= 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['monthlyDebtService'],
          message: 'La cuota mensual es obligatoria',
        });
      }
      return;
    }

    if (data.loanAmount === null || data.loanAmount <= 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['loanAmount'],
        message: 'El importe financiado es obligatorio',
      });
    }

    if (data.termYears === null || data.termYears <= 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['termYears'],
        message: 'El plazo es obligatorio',
      });
    }

    if (!data.rateType) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['rateType'],
        message: 'El tipo de interés es obligatorio',
      });
    }

    if (data.rateType === 'FIXED' && data.fixedRate === null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['fixedRate'],
        message: 'El TIN fijo es obligatorio',
      });
    }

    if (data.rateType === 'VARIABLE') {
      if (data.variableEuribor === null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['variableEuribor'],
          message: 'El Euríbor es obligatorio',
        });
      }

      if (data.variableSpread === null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['variableSpread'],
          message: 'El diferencial es obligatorio',
        });
      }
    }
  });

export type InvestmentFormData = z.infer<typeof investmentFormSchema>;

export const getEmptyInvestmentFormData = (): InvestmentFormData => ({
  alias: '',
  identificationCode: '',
  country: 'ES',
  address: '',
  city: '',
  postalCode: '',
  province: '',
  type: 'TRADITIONAL_RENT',
  status: 'PURCHASING',
  purchaseDate: '',
  ownershipPercentage: 1,
  acquisitionPrice: null,
  renovationCosts: null,
  furnitureEquipmentCosts: null,
  initialCostComplete: false,
  purchaseCostsTotal: null,
  itpTax: null,
  notaryFees: null,
  registryFees: null,
  agencyFees: null,
  legalFees: null,
  otherTransactionCosts: null,
  rentedSince: '',
  monthlyRent: null,
  monthlyOpexTotal: null,
  ibiTax: null,
  communityFee: null,
  supplyCosts: null,
  maintenanceCosts: null,
  wasteTax: null,
  homeInsurance: null,
  rentInsurance: null,
  otherOpex: null,
  financingDecision: 'PENDING',
  financingInputMode: '',
  monthlyDebtService: null,
  loanAmount: null,
  termYears: null,
  rateType: '',
  fixedRate: null,
  variableSpread: null,
  variableEuribor: null,
  mainImageFile: null,
  removeMainImage: false,
  mainImageUrl: undefined,
});
